import React, { useContext, useEffect, useState } from "react";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import useOrders from "features/tradingTerminal/Orders/useOrders";
import { CreateTradeSections } from "@shared/features/tradingTerminal/common/types";
import MiniGeneralTradeInfo from "features/tradingTerminal/General/MiniGeneralTradeInfo";
import General from "features/tradingTerminal/General/General";
import { OrderTypes } from "@shared/types";
import useGeneralConfiguration from "features/configuration/GeneralConfiguration/useGeneralConfiguration";
import { useSelector } from "react-redux";
import LayoutSwitch from "common/Switch/LayoutSwitch";
import { FormGraphLayoutIcon, GridLayoutIcon } from "common/Icons/Icons";
import localStorageService from "utils/localStorageService";
import { TRADING_TERMINAL_OLD_VERSION_KEY } from "@shared/features/tradingTerminal/common/constants";
import { MAIN_CONTENT_ID } from "common/constants";
import useInitializeTrailingStop from "@shared/features/tradingTerminal/common/hooks/useInitializeTrailingStop";
import { sharedLib } from "@shared/services/shared.service";
import { usePageIntro } from "common/NewLook";

const useWebTradingTerminalProvider = () => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const { setTitleExtraComponent } = useContext(PageWrapperContext);
  const {
    useTradingTerminalOldVersion,
    setUseTradingTerminalOldVersion,
    isMobile,
    isError,
    refetchErrorQueries,
    isSignal,
  } = useContext(TradingTerminalContext)!;
  const isOldVersion =
    !isSignal && localStorageService.getValue(TRADING_TERMINAL_OLD_VERSION_KEY);
  const [noExchangeModalVisible, setNoExchangeModalVisible] = useState(false);

  const orderTypeToOrdersSection = useOrders();
  const formattedTrailingStopItem = useInitializeTrailingStop();

  const {
    generalConfiguration: trailingComponent,
    modifiedConfiguration: trailingModifiedConfiguration,
  } = useGeneralConfiguration({
    configuration: formattedTrailingStopItem,
    showOverrides: false,
    colorModifiedValues: false,
    useCard: false,
    isForm: true,
    spacing: 16,
  });

  useEffect(() => {
    if (isOldVersion !== null) {
      setUseTradingTerminalOldVersion(isOldVersion === "true");
    }
  }, []);

  useEffect(() => {
    if (isMobile) {
      setUseTradingTerminalOldVersion(false);
      localStorageService.setValue(TRADING_TERMINAL_OLD_VERSION_KEY, false);
    }
  }, [isMobile]);

  const onClick = () => {
    setUseTradingTerminalOldVersion(!useTradingTerminalOldVersion);
    localStorageService.setValue(
      TRADING_TERMINAL_OLD_VERSION_KEY,
      !useTradingTerminalOldVersion
    );
    const scrollingElement = document.getElementById(MAIN_CONTENT_ID);
    if (scrollingElement && scrollingElement.scrollTop >= 1) {
      scrollingElement.scroll(0, 0);
    }
  };

  const layoutSwitch = (
    <LayoutSwitch
      layoutIcons={[<FormGraphLayoutIcon />, <GridLayoutIcon />]}
      selectedLayoutIndex={isOldVersion === "true" ? 1 : 0}
      onClick={onClick}
      style={{ marginLeft: 16 }}
      iconBgStyle={{ padding: "4px 5px" }}
    />
  );

  useEffect(() => {
    setTitleExtraComponent(isSignal ? undefined : layoutSwitch);
  }, [useTradingTerminalOldVersion, isSignal]);

  // New look: the terminal is a full-height tool, so its page intro keeps
  // only the title row (with the layout switch) to leave room for the chart.
  usePageIntro({ description: "" }, []);

  const sectionToComponent: Record<CreateTradeSections, React.JSX.Element> = {
    [CreateTradeSections.GENERAL]: useTradingTerminalOldVersion ? (
      <MiniGeneralTradeInfo />
    ) : (
      <General />
    ),
    [CreateTradeSections.ENTRIES]: orderTypeToOrdersSection[OrderTypes.ENTRY],
    [CreateTradeSections.TAKE_PROFITS]:
      orderTypeToOrdersSection[OrderTypes.TAKE_PROFIT],
    [CreateTradeSections.STOP]: orderTypeToOrdersSection[OrderTypes.STOP],
  };

  useEffect(() => {
    if (!user?.has_valid_clients_connected && !isSignal) {
      setNoExchangeModalVisible(true);
    }
  }, [user]);

  return {
    sectionToComponent,
    trailingComponent,
    trailingModifiedConfiguration,
    useTradingTerminalOldVersion,
    noExchangeModalVisible,
    setNoExchangeModalVisible,
    isError,
    refetchErrorQueries,
  };
};

export default useWebTradingTerminalProvider;
